import { useState } from "react";
import {
  Alert, Box, Button, Chip, Paper, Stack, TextField,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { narcoticApi, type NarcoticRegisterRow } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { PageHeader } from "@/components/PageHeader";
import { formatDateTime, todayIso } from "@/shared/datetime";

/**
 * S14 — Narcotic / schedule-drug register. Projected from the immutable ledger so it
 * can never drift from actual stock movements. CSV export for drug-inspector audits.
 */
export function NarcoticRegisterPage() {
  const canExport = useAuth(s => s.hasPermission("clinical.narcotic.export"));
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const query = useQuery({
    queryKey: ["narcotic-register", from, to],
    queryFn: () => narcoticApi.list({ from: from || undefined, to: to || undefined }),
  });

  const columns: GridColDef<NarcoticRegisterRow>[] = [
    { field: "at", headerName: "Date/time", width: 170,
      valueFormatter: (v) => v ? formatDateTime(v as string) : "" },
    { field: "sku", headerName: "SKU", width: 120 },
    { field: "name", headerName: "Drug", flex: 1, minWidth: 200 },
    { field: "scheduleCategory", headerName: "Sched", width: 80,
      renderCell: (p) => p.value ? <Chip size="small" color="warning" label={p.value as string} /> : null },
    { field: "batchNo", headerName: "Batch", width: 130 },
    { field: "movement", headerName: "Movement", width: 130 },
    { field: "qtyIn", headerName: "In", width: 80, type: "number" },
    { field: "qtyOut", headerName: "Out", width: 80, type: "number" },
    { field: "runningBalance", headerName: "Balance", width: 100, type: "number" },
    { field: "referenceType", headerName: "Ref", width: 90 },
  ];

  const exportCsv = () => {
    const rows = query.data ?? [];
    const header = ["DateTime", "SKU", "Drug", "Schedule", "Batch", "Movement", "In", "Out", "Balance", "Ref"];
    const lines = rows.map(r => [
      new Date(r.at).toISOString(), r.sku, `"${r.name}"`, r.scheduleCategory ?? "",
      r.batchNo, r.movement, r.qtyIn, r.qtyOut, r.runningBalance, r.referenceType ?? "",
    ].join(","));
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `narcotic-register-${todayIso()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Inventory", "Narcotic register"]}
        title="Narcotic register"
        actions={
          canExport ? (
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!query.data?.length}>
              Export CSV
            </Button>
          ) : undefined
        }
      />
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField type="date" label="From" value={from} size="small"
            InputLabelProps={{ shrink: true }} onChange={(e) => setFrom(e.target.value)} />
          <TextField type="date" label="To" value={to} size="small"
            InputLabelProps={{ shrink: true }} onChange={(e) => setTo(e.target.value)} />
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data ?? []} columns={columns} loading={query.isLoading}
            disableRowSelectionOnClick getRowId={(row) => `${row.itemId}-${row.at}-${row.batchNo}-${row.movement}-${row.runningBalance}`}
          />
        </Box>
      </Paper>
    </Box>
  );
}
